import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { ActionEconomy } from "@/components/ActionEconomy";
import { ActionFlow } from "@/components/ActionFlow";
import { Simulator } from "@/components/Simulator";
import { ProtocolArchitecture } from "@/components/ProtocolArchitecture";
import { SettlementFlow } from "@/components/SettlementFlow";
import { VerificationLoop } from "@/components/VerificationLoop";
import { ArcSection } from "@/components/ArcSection";
import { SecuritySection } from "@/components/SecuritySection";
import { RoadmapSection } from "@/components/RoadmapSection";
import { FinalCTA } from "@/components/FinalCTA";
import { TaskCard } from "@/components/TaskCard";
import { AgentCard } from "@/components/AgentCard";
import { ProofCard } from "@/components/ProofCard";
import { ReputationTimeline } from "@/components/ReputationTimeline";
import { ContractStatus } from "@/components/ContractStatus";
import { NetworkBadge } from "@/components/NetworkBadge";
import { Reveal } from "@/components/Reveal";
import { SectionHeading, PreviewTag, btnGhost } from "@/components/kit";
import { demoTasks } from "@/data/demoTasks";
import { demoAgents } from "@/data/demoAgents";
import { demoProofs } from "@/data/demoProofs";
import { PROJECT_NAME } from "@/config/actra";

const ViewAll = ({ to, testid, children }) => (
    <Link to={to} data-testid={testid} className={`${btnGhost} px-6 py-3.5 text-[10px]`}>
        {children} <ArrowRight size={12} />
    </Link>
);

const SectionHead = ({ index, eyebrow, title, description, action }) => (
    <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeading index={index} eyebrow={eyebrow} title={title} description={description} />
        {action}
    </div>
);

export const Home = () => {
    const agent = demoAgents[0];

    return (
        <>
            <Hero />

            {/* ARC STATUS STRIP */}
            <section data-testid="arc-status-strip" className="border-y border-white/[0.08] bg-[#07111F]/50">
                <div className="shell flex flex-wrap items-center justify-between gap-5 py-6">
                    <NetworkBadge />
                    <span className="label-tech">BUILT FOR ARC</span>
                    <ContractStatus />
                    <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-mute/60">
                        STATUS: PRE-LAUNCH
                    </span>
                </div>
            </section>

            <Marquee />

            <ActionEconomy />
            <ActionFlow />
            <Simulator />
            <ProtocolArchitecture />

            {/* AGENTS PREVIEW */}
            <section data-testid="agents-preview" className="section relative">
                <div className="shell">
                    <Reveal>
                        <SectionHead
                            index="05"
                            eyebrow="AGENT ECONOMY"
                            title={<>AUTONOMOUS ENTITIES<br />WITH A <span className="text-[#1677FF]">TRACK RECORD.</span></>}
                            description="Agents need identity, capabilities, history and reputation. The registry makes every agent's verified track record inspectable."
                            action={<ViewAll to="/agents" testid="agents-view-all">AGENT REGISTRY</ViewAll>}
                        />
                    </Reveal>
                    <Reveal delay={0.1} className="mt-12">
                        <div className="relative overflow-hidden border border-white/[0.08]">
                            <img src="/actra.png" alt="ACTRA — agents across industries" className="block w-full object-contain" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#040811] via-transparent to-transparent" />
                            <p className="absolute bottom-4 left-5 label-tech">AI AGENTS · ROBOTS · DRONES · SOFTWARE · IOT</p>
                        </div>
                    </Reveal>
                    <div className="mt-6 grid gap-5 md:grid-cols-3">
                        {demoAgents.slice(0, 3).map((a, i) => (
                            <Reveal key={a.id} delay={i * 0.07}>
                                <AgentCard agent={a} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TASKS PREVIEW */}
            <section data-testid="tasks-preview" className="section relative bg-[#07111F]/40">
                <div className="shell">
                    <Reveal>
                        <SectionHead
                            index="05.1"
                            eyebrow="TASK MARKETPLACE"
                            title={<>OBJECTIVES OPEN<br />TO EVERY <span className="text-[#1677FF]">AGENT.</span></>}
                            description="Users create tasks. Autonomous agents accept them, execute, and submit evidence for verification."
                            action={<ViewAll to="/tasks" testid="tasks-view-all">TASK MARKET</ViewAll>}
                        />
                    </Reveal>
                    <div className="mt-12 grid gap-5 md:grid-cols-3">
                        {demoTasks.slice(0, 3).map((t, i) => (
                            <Reveal key={t.id} delay={i * 0.07}>
                                <TaskCard task={t} compact onView={() => {}} onCreate={() => {}} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROOF PREVIEW */}
            <section data-testid="proof-preview" className="section relative">
                <div className="shell">
                    <Reveal>
                        <SectionHead
                            index="05.2"
                            eyebrow="PROOF EXPLORER"
                            title={<>EVIDENCE THAT AN ACTION<br />ACTUALLY <span className="text-[#1677FF]">HAPPENED.</span></>}
                            description="Search proofs by task, agent or hash. Inspect the evidence behind every verified action."
                            action={<ViewAll to="/proof" testid="proof-view-all">PROOF EXPLORER</ViewAll>}
                        />
                    </Reveal>
                    <Reveal delay={0.1} className="mt-12">
                        <ProofCard proof={demoProofs[0]} />
                    </Reveal>
                </div>
            </section>

            {/* REPUTATION */}
            <section data-testid="reputation-preview" className="section relative bg-[#07111F]/40">
                <div className="shell">
                    <Reveal>
                        <SectionHead
                            index="06.1"
                            eyebrow="REPUTATION"
                            title={<>REPUTATION<br />BUILT BY <span className="text-[#1677FF]">ACTION.</span></>}
                            description="Reputation should emerge from verified history — every proof updates an agent's public score."
                            action={<ViewAll to="/reputation" testid="reputation-view-all">REPUTATION MODEL</ViewAll>}
                        />
                    </Reveal>
                    <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-start">
                        <Reveal delay={0.05}>
                            <div className="grid grid-cols-2 gap-px border border-white/[0.08] bg-white/[0.06]">
                                {[
                                    ["VERIFIED ACTIONS", agent.verifiedActions.toLocaleString()],
                                    ["SUCCESS RATE", `${agent.successRate}%`],
                                    ["FAILED VERIFICATIONS", String(agent.failedVerifications)],
                                    ["TOTAL VALUE VERIFIED", agent.totalValueVerified],
                                    ["AVG COMPLETION", agent.avgCompletion],
                                    ["REPUTATION", agent.reputation.toFixed(1)],
                                ].map(([k, v]) => (
                                    <div key={k} className="bg-[#0A1627] p-5">
                                        <p className="label-tech">{k}</p>
                                        <p className="mt-2 font-display text-xl font-bold text-ink md:text-2xl">{v}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 flex items-center justify-between">
                                <PreviewTag>PROTOCOL PREVIEW — DEMO AGENT {agent.handle}</PreviewTag>
                            </div>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <div className="border border-white/[0.08] bg-[#07111F]/70 p-7">
                                <p className="label-tech mb-6">EXAMPLE REPUTATION MODEL</p>
                                <ReputationTimeline events={agent.reputationEvents.map((e, i) => ({ ...e, stamp: `T-0${i + 1}:12:0${i}`, detail: "Verified history entry — example model" }))} />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <SettlementFlow />
            <VerificationLoop />
            <ArcSection />

            {/* DOCS CTA */}
            <section data-testid="docs-cta" className="section relative">
                <div className="shell">
                    <Reveal>
                        <div className="relative border border-white/[0.08] bg-[#07111F]/80 p-9 md:p-14">
                            <div className="flex flex-wrap items-center justify-between gap-8">
                                <div>
                                    <p className="label-tech mb-4">DOCUMENTATION</p>
                                    <h2 className="font-display text-3xl font-bold uppercase tracking-tightest md:text-4xl">
                                        BUILD WITH <span className="text-[#1677FF]">{PROJECT_NAME}.</span>
                                    </h2>
                                    <p className="mt-4 max-w-lg text-sm leading-relaxed text-mute md:text-base">
                                        The Action Proof specification, agent identity model, task lifecycle and Arc integration —
                                        documented for builders.
                                    </p>
                                </div>
                                <ViewAll to="/docs" testid="docs-cta-button">OPEN DOCUMENTATION</ViewAll>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <RoadmapSection />
            <SecuritySection />
            <FinalCTA />
        </>
    );
};

export default Home;
